import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  createScalarDraftAdapter,
  defineNavigationQuerySchema,
  navigationQuery,
  useNavigationScope,
  useNavigationQuery,
} from '../../navigation';

export type FinanceDialog = '' | 'income' | 'expense' | 'monthly-expense' | 'report' | 'income-history' | 'expense-history';
export type FinanceReceiptId = `income:${number}` | `expense:${number}`;
export type SalesInvoiceStatus = 'pending_review' | 'approved' | 'rejected';
export type SalesInvoicePanel = '' | 'approve' | 'edit' | 'receipt';
export type PlacementPaymentStatus = '' | 'pending' | 'approved' | 'rejected';
export type PlacementPaymentPanel = 'details' | 'reject';
export type RefundStatus = 'pending' | 'reception_approved' | 'reception_rejected' | 'scheduled';
export type RefundPanel = '' | 'details' | 'approve' | 'reject';

const financeDialogs = ['', 'income', 'expense', 'monthly-expense', 'report', 'income-history', 'expense-history'] as const;

export const financeSchema = defineNavigationQuerySchema({
  monthToDate: navigationQuery.enum('monthToDate', ['', '1'] as const, ''),
  from: navigationQuery.date('from'),
  to: navigationQuery.date('to'),
  days: navigationQuery.integer('days', { defaultValue: 30, min: 1, max: 30 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  dialog: navigationQuery.enum('dialog', financeDialogs, ''),
  editExpense: navigationQuery.id('editExpense'),
  receipt: navigationQuery.string('receipt', { defaultValue: '', maxLength: 40 }),
});

export const branchFinanceSchema = defineNavigationQuerySchema({
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  dialog: navigationQuery.enum('dialog', financeDialogs, ''),
  editExpense: navigationQuery.id('editExpense'),
});

export const salesInvoicesSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum(
    'status',
    ['pending_review', 'approved', 'rejected'] as const,
    'pending_review',
  ),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  hasStudent: navigationQuery.enum('hasStudent', ['yes', 'no', 'all'] as const, 'yes'),
  invoice: navigationQuery.id('invoice'),
  invoicePanel: navigationQuery.enum(
    'invoicePanel',
    ['', 'approve', 'edit', 'receipt'] as const,
    '',
  ),
});

export const proFinanceSchema = defineNavigationQuerySchema({
  days: navigationQuery.integer('days', { defaultValue: 30, min: 1, max: 90 }),
  tab: navigationQuery.enum('tab', ['invoices', 'trend'] as const, 'invoices'),
  student: navigationQuery.id('student'),
  page: navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 9999 }),
  custom: navigationQuery.boolean('custom', false),
  from: navigationQuery.date('from'),
  to: navigationQuery.date('to'),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  report: navigationQuery.boolean('report', false),
});

export const placementPaymentsSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum(
    'status',
    ['', 'pending', 'approved', 'rejected'] as const,
    'pending',
  ),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  method: navigationQuery.enum(
    'method',
    ['', 'system_free_offer', 'cash', 'branch', 'vodafone_cash', 'instapay', 'bank_account'] as const,
    '',
  ),
  page: navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 9999 }),
  pageSize: navigationQuery.enum('pageSize', ['10', '20', '50'] as const, '20'),
  payment: navigationQuery.id('payment'),
  paymentPanel: navigationQuery.enum('paymentPanel', ['details', 'reject'] as const, 'details'),
});

export const refundsSchema = defineNavigationQuerySchema({
  status: navigationQuery.enum(
    'status',
    ['pending', 'reception_approved', 'reception_rejected', 'scheduled'] as const,
    'pending',
  ),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 80 }),
  page: navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 9999 }),
  refund: navigationQuery.id('refund'),
  refundPanel: navigationQuery.enum(
    'refundPanel',
    ['', 'details', 'approve', 'reject'] as const,
    '',
  ),
});

export const receptionFinanceDraftAdapters = Object.freeze({
  money: createScalarDraftAdapter<{
    paidDate: string;
    reportingPeriod: string;
    reportingMonth: string;
    description: string;
    amount: string;
    category: string;
    paymentMethod: string;
    branch: string;
    costCenter: string;
    notes: string;
    branchCleared: boolean;
    costCenterCleared: boolean;
    categoryCleared: boolean;
    paymentMethodCleared: boolean;
  }>({
    paidDate: '',
    reportingPeriod: 'daily',
    reportingMonth: '',
    description: '',
    amount: '',
    category: '',
    paymentMethod: '',
    branch: '',
    costCenter: '',
    notes: '',
    branchCleared: false,
    costCenterCleared: false,
    categoryCleared: false,
    paymentMethodCleared: false,
  }),
  report: createScalarDraftAdapter<{
    rangeType: string;
    branch: string;
    customStart: string;
    customEnd: string;
  }>({
    rangeType: 'today',
    branch: '',
    customStart: '',
    customEnd: '',
  }),
  salesApproval: createScalarDraftAdapter<{ comment: string }>({ comment: '' }),
  invoiceEdit: createScalarDraftAdapter<{ paymentMethod: string; paymentMethodCleared: boolean }>({ paymentMethod: '', paymentMethodCleared: false }),
  refundApproval: createScalarDraftAdapter<{
    approvedAmount: string;
    note: string;
  }>({ approvedAmount: '', note: '' }),
});

export function isReceptionFinanceShellAllowed(user: {
  role?: string | null;
  username?: string | null;
}): boolean {
  const role = String(user.role ?? '').trim().toLowerCase();
  return role === 'super_reception'
    || role === 'general_manager'
    || role === 'manager';
}

export function useReceptionFinanceDebouncedSearch(
  canonicalValue: string,
  onCommit: (value: string) => void,
  delay = 350,
): readonly [string, React.Dispatch<React.SetStateAction<string>>] {
  const location = useLocation();
  const [value, setValue] = React.useState(canonicalValue);
  const commitRef = React.useRef(onCommit);
  const observedLocationKeyRef = React.useRef(location.key);
  commitRef.current = onCommit;

  React.useEffect(() => {
    if (observedLocationKeyRef.current !== location.key) {
      observedLocationKeyRef.current = location.key;
      setValue(canonicalValue);
      return;
    }
    if (value === canonicalValue) return;
    const sourceLocationKey = location.key;
    const timer = window.setTimeout(() => {
      if (observedLocationKeyRef.current !== sourceLocationKey) return;
      commitRef.current(value);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [canonicalValue, delay, location.key, value]);

  return [value, setValue] as const;
}

export type ReceptionFinanceMutationCurrentness = () => boolean;

/**
 * Bind an async mutation continuation to the exact authorized navigation view
 * that started it. Server work may finish after navigation, but UI effects from
 * that obsolete continuation must not touch the newer view.
 */
export function useReceptionFinanceMutationGuard(
  entityIdentity: string | number | null = null,
): () => ReceptionFinanceMutationCurrentness {
  const location = useLocation();
  const scope = useNavigationScope();
  const identity = JSON.stringify([
    scope.scopeId,
    scope.userId,
    location.key,
    location.pathname,
    location.search,
    location.hash,
    entityIdentity,
  ]);
  const identityRef = React.useRef(identity);
  const mountedRef = React.useRef(false);
  const operationGenerationRef = React.useRef(0);
  identityRef.current = identity;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
    };
  }, []);

  return React.useCallback(() => {
    const capturedIdentity = identityRef.current;
    const capturedOperationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = capturedOperationGeneration;
    return () => mountedRef.current
      && operationGenerationRef.current === capturedOperationGeneration
      && identityRef.current === capturedIdentity;
  }, []);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;
const POSITIVE_ID = /^[1-9]\d*$/u;
const FINANCE_RECEIPT = /^(?:income|expense):[1-9]\d*$/u;

export function sanitizeReceptionFinanceAssetUrl(
  value: string | null | undefined,
  baseUrl = typeof window === 'undefined' ? 'http://localhost/' : window.location.origin,
): string | null {
  const candidate = String(value ?? '').trim();
  if (!candidate) return null;
  if (candidate.replace(/\\/gu, '/').startsWith('//')) return null;
  try {
    const parsed = new URL(candidate, baseUrl);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

export function sanitizeReceptionFinanceInternalPath(value: string | null | undefined): string | null {
  const candidate = String(value ?? '').trim();
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;
  try {
    const parsed = new URL(candidate, 'https://navigation.invalid');
    if (parsed.origin !== 'https://navigation.invalid') return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function validDate(value: string | null): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function boundedString(params: URLSearchParams, key: string, maxLength: number): string {
  const value = params.get(key) ?? '';
  return value.length <= maxLength ? value : '';
}

function positiveId(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (!raw || !POSITIVE_ID.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

function boundedInteger(
  params: URLSearchParams,
  key: string,
  defaultValue: number,
  min: number,
  max: number,
): number {
  const raw = params.get(key);
  if (!raw || !/^(?:0|[1-9]\d*)$/u.test(raw)) return defaultValue;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= min && value <= max ? value : defaultValue;
}

function booleanParam(params: URLSearchParams, key: string): boolean {
  return params.get(key) === '1';
}

function enumParam<const T extends readonly string[]>(
  params: URLSearchParams,
  key: string,
  allowed: T,
  fallback: T[number],
): T[number] {
  const value = params.get(key);
  return value !== null && (allowed as readonly string[]).includes(value)
    ? value as T[number]
    : fallback;
}

function removeInvalidLength(params: URLSearchParams, key: string, maxLength: number) {
  const value = params.get(key);
  if (value !== null && value.length > maxLength) params.delete(key);
}

function removeInvalidInteger(
  params: URLSearchParams,
  key: string,
  min: number,
  max: number,
  defaultValue?: number,
) {
  const raw = params.get(key);
  if (raw === null) return;
  const value = /^(?:0|[1-9]\d*)$/u.test(raw) ? Number(raw) : Number.NaN;
  if (!Number.isSafeInteger(value) || value < min || value > max || value === defaultValue) {
    params.delete(key);
  }
}

function removeInvalidId(params: URLSearchParams, key: string) {
  if (params.has(key) && positiveId(params, key) === null) params.delete(key);
}

function removeDefault(params: URLSearchParams, key: string, defaultValue: string) {
  if (params.get(key) === defaultValue) params.delete(key);
}

function moveAlias(params: URLSearchParams, alias: string, key: string) {
  if (!params.has(key) && params.has(alias)) params.set(key, params.get(alias) ?? '');
  params.delete(alias);
}

function normalizeCommonSearch(params: URLSearchParams) {
  removeInvalidLength(params, 'q', 120);
  removeInvalidLength(params, 'branch', 80);
}

export type ReceptionFinanceNavigationKind =
  | 'finance'
  | 'branch-finance'
  | 'sales-invoices'
  | 'pro-finance'
  | 'placement-payments'
  | 'refunds';

export function canonicalizeReceptionFinanceSearch(
  search: string,
  kind: ReceptionFinanceNavigationKind,
): string {
  const params = new URLSearchParams(search);

  if (kind === 'finance' || kind === 'branch-finance') {
    removeInvalidId(params, 'editExpense');
    if (['income','expense','monthly-expense'].includes(params.get('dialog') ?? '') || params.has('receipt')) params.delete('editExpense');
  }
  if (kind === 'finance') {
    const from=params.get('from'), to=params.get('to');
    if (!validDate(from) || !validDate(to) || from.slice(0,4)<'1900' || to.slice(0,4)>'2100' || from>to || (Date.parse(to)-Date.parse(from))/86400000>365) {
      params.delete('from'); params.delete('to');
    }
    if(params.get('monthToDate')!=='1'||!params.has('from')||!params.has('to')||!from?.endsWith('-01')||from.slice(0,7)!==to?.slice(0,7)) params.delete('monthToDate');
    removeInvalidInteger(params, 'days', 1, 30, 30);
    removeInvalidLength(params, 'branch', 80);
    const dialog = params.get('dialog');
    if (dialog !== null && !financeDialogs.includes(dialog as FinanceDialog)) params.delete('dialog');
    const receipt = params.get('receipt');
    if (receipt !== null && !FINANCE_RECEIPT.test(receipt)) params.delete('receipt');
    if (params.has('receipt')) params.delete('dialog');
    removeDefault(params, 'dialog', '');
  } else if (kind === 'branch-finance') {
    removeInvalidLength(params, 'branch', 80);
    const dialog = params.get('dialog');
    if (dialog !== null && !financeDialogs.includes(dialog as FinanceDialog)) params.delete('dialog');
    removeDefault(params, 'dialog', '');
  } else if (kind === 'sales-invoices') {
    moveAlias(params, 'has_student', 'hasStudent');
    if (params.get('status') === 'pending') params.set('status', 'pending_review');
    normalizeCommonSearch(params);
    const status = params.get('status');
    if (status !== null && !['pending_review', 'approved', 'rejected'].includes(status)) params.delete('status');
    removeDefault(params, 'status', 'pending_review');
    const hasStudent = params.get('hasStudent');
    if (hasStudent !== null && !['yes', 'no', 'all'].includes(hasStudent)) params.delete('hasStudent');
    removeDefault(params, 'hasStudent', 'yes');
    removeInvalidId(params, 'invoice');
    const panel = params.get('invoicePanel');
    if (panel !== null && !['approve', 'edit', 'receipt'].includes(panel)) params.delete('invoicePanel');
    if (!params.has('invoice') || !params.has('invoicePanel')) {
      params.delete('invoice');
      params.delete('invoicePanel');
    } else if (params.get('status') && params.get('status') !== 'pending_review'
      && ['approve', 'edit'].includes(params.get('invoicePanel') ?? '')) {
      params.delete('invoice');
      params.delete('invoicePanel');
    }
  } else if (kind === 'pro-finance') {
    moveAlias(params, 'start_date', 'from');
    moveAlias(params, 'end_date', 'to');
    removeInvalidInteger(params, 'days', 1, 90, 30);
    removeInvalidInteger(params, 'page', 1, 9999, 1);
    normalizeCommonSearch(params);
    const tab = params.get('tab');
    if (tab !== null && !['invoices', 'trend'].includes(tab)) params.delete('tab');
    removeDefault(params, 'tab', 'invoices');
    removeInvalidId(params, 'student');
    for (const key of ['from', 'to']) {
      if (params.has(key) && !validDate(params.get(key))) params.delete(key);
    }
    const custom = params.get('custom') === '1';
    const from = params.get('from');
    const to = params.get('to');
    if (!custom || !from || !to || from > to) {
      params.delete('custom');
      params.delete('from');
      params.delete('to');
    } else {
      params.delete('days');
    }
    if (params.get('tab') === 'trend') params.delete('page');
    if (params.has('student')) params.delete('report');
    if (params.get('report') !== '1') params.delete('report');
  } else if (kind === 'placement-payments') {
    moveAlias(params, 'page_size', 'pageSize');
    if (params.get('status') === 'confirmed') params.set('status', 'approved');
    if (params.get('method') === 'bank_transfer') params.set('method', 'bank_account');
    normalizeCommonSearch(params);
    const status = params.get('status');
    if (status !== null && !['pending', 'approved', 'rejected', ''].includes(status)) params.delete('status');
    removeDefault(params, 'status', 'pending');
    const method = params.get('method');
    if (method !== null && !['system_free_offer', 'cash', 'branch', 'vodafone_cash', 'instapay', 'bank_account'].includes(method)) {
      params.delete('method');
    }
    removeInvalidInteger(params, 'page', 1, 9999, 1);
    if (!['10', '20', '50'].includes(params.get('pageSize') ?? '20')) params.delete('pageSize');
    removeDefault(params, 'pageSize', '20');
    removeInvalidId(params, 'payment');
    const panel = params.get('paymentPanel');
    if (panel !== null && !['details', 'reject'].includes(panel)) params.delete('paymentPanel');
    if (!params.has('payment')) params.delete('paymentPanel');
    removeDefault(params, 'paymentPanel', 'details');
  } else {
    moveAlias(params, 'refund_panel', 'refundPanel');
    if (params.get('status') === 'approved') params.set('status', 'reception_approved');
    if (params.get('status') === 'rejected') params.set('status', 'reception_rejected');
    normalizeCommonSearch(params);
    const status = params.get('status');
    if (status !== null && !['pending', 'reception_approved', 'reception_rejected', 'scheduled'].includes(status)) params.delete('status');
    removeDefault(params, 'status', 'pending');
    removeInvalidInteger(params, 'page', 1, 9999, 1);
    removeInvalidId(params, 'refund');
    const panel = params.get('refundPanel');
    if (panel !== null && !['details', 'approve', 'reject'].includes(panel)) params.delete('refundPanel');
    if (!params.has('refund') || !params.has('refundPanel')) {
      params.delete('refund');
      params.delete('refundPanel');
    } else if (params.get('status') && params.get('status') !== 'pending'
      && ['approve', 'reject'].includes(params.get('refundPanel') ?? '')) {
      params.delete('refund');
      params.delete('refundPanel');
    }
  }

  const value = params.toString();
  return value ? `?${value}` : '';
}

function useCanonicalSearch(kind: ReceptionFinanceNavigationKind): string {
  const location = useLocation();
  const navigate = useNavigate();
  const canonicalSearch = React.useMemo(
    () => canonicalizeReceptionFinanceSearch(location.search, kind),
    [kind, location.search],
  );
  React.useEffect(() => {
    if (canonicalSearch === location.search) return;
    void navigate({
      pathname: location.pathname,
      search: canonicalSearch,
      hash: location.hash,
    }, { replace: true, state: location.state });
  }, [canonicalSearch, location.hash, location.pathname, location.search, location.state, navigate]);
  return canonicalSearch;
}

export function useFinanceNavigation() {
  const canonicalSearch = useCanonicalSearch('finance');
  const navigation = useNavigationQuery(financeSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  const receipt = params.get('receipt');
  return React.useMemo(() => ({
    ...navigation,
    values: {
      days: boundedInteger(params, 'days', 30, 1, 30),
      monthToDate: enumParam(params, 'monthToDate', ['', '1'] as const, ''),
      from: boundedString(params, 'from', 10),
      to: boundedString(params, 'to', 10),
      editExpense: positiveId(params, 'editExpense'),
      branch: boundedString(params, 'branch', 80),
      dialog: enumParam(params, 'dialog', financeDialogs, ''),
      receipt: receipt && FINANCE_RECEIPT.test(receipt) ? receipt as FinanceReceiptId : '',
    },
  }), [navigation, params, receipt]);
}

export function useBranchFinanceNavigation() {
  const canonicalSearch = useCanonicalSearch('branch-finance');
  const navigation = useNavigationQuery(branchFinanceSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      editExpense: positiveId(params, 'editExpense'),
      branch: boundedString(params, 'branch', 80),
      dialog: enumParam(params, 'dialog', financeDialogs, ''),
    },
  }), [navigation, params]);
}

export function useSalesInvoicesNavigation() {
  const canonicalSearch = useCanonicalSearch('sales-invoices');
  const navigation = useNavigationQuery(salesInvoicesSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      status: enumParam(params, 'status', ['pending_review', 'approved', 'rejected'] as const, 'pending_review'),
      q: boundedString(params, 'q', 120),
      branch: boundedString(params, 'branch', 80),
      hasStudent: enumParam(params, 'hasStudent', ['yes', 'no', 'all'] as const, 'yes'),
      invoice: positiveId(params, 'invoice'),
      invoicePanel: enumParam(params, 'invoicePanel', ['', 'approve', 'edit', 'receipt'] as const, ''),
    },
  }), [navigation, params]);
}

export function useProFinanceNavigation() {
  const canonicalSearch = useCanonicalSearch('pro-finance');
  const navigation = useNavigationQuery(proFinanceSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      days: boundedInteger(params, 'days', 30, 1, 90),
      tab: enumParam(params, 'tab', ['invoices', 'trend'] as const, 'invoices'),
      student: positiveId(params, 'student'),
      page: boundedInteger(params, 'page', 1, 1, 9999),
      custom: booleanParam(params, 'custom'),
      from: validDate(params.get('from')) ? params.get('from') : null,
      to: validDate(params.get('to')) ? params.get('to') : null,
      branch: boundedString(params, 'branch', 80),
      q: boundedString(params, 'q', 120),
      report: booleanParam(params, 'report'),
    },
  }), [navigation, params]);
}

export function usePlacementPaymentsNavigation() {
  const canonicalSearch = useCanonicalSearch('placement-payments');
  const navigation = useNavigationQuery(placementPaymentsSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      status: enumParam(params, 'status', ['', 'pending', 'approved', 'rejected'] as const, 'pending'),
      q: boundedString(params, 'q', 120),
      branch: boundedString(params, 'branch', 80),
      method: enumParam(params, 'method', ['', 'system_free_offer', 'cash', 'branch', 'vodafone_cash', 'instapay', 'bank_account'] as const, ''),
      page: boundedInteger(params, 'page', 1, 1, 9999),
      pageSize: enumParam(params, 'pageSize', ['10', '20', '50'] as const, '20'),
      payment: positiveId(params, 'payment'),
      paymentPanel: enumParam(params, 'paymentPanel', ['details', 'reject'] as const, 'details'),
    },
  }), [navigation, params]);
}

export function useRefundsNavigation() {
  const canonicalSearch = useCanonicalSearch('refunds');
  const navigation = useNavigationQuery(refundsSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      status: enumParam(params, 'status', ['pending', 'reception_approved', 'reception_rejected', 'scheduled'] as const, 'pending'),
      q: boundedString(params, 'q', 120),
      branch: boundedString(params, 'branch', 80),
      page: boundedInteger(params, 'page', 1, 1, 9999),
      refund: positiveId(params, 'refund'),
      refundPanel: enumParam(params, 'refundPanel', ['', 'details', 'approve', 'reject'] as const, ''),
    },
  }), [navigation, params]);
}

export const receptionFinanceNavigationInternals = Object.freeze({
  validDate,
  canonicalizeReceptionFinanceSearch,
});
